import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Plus, Pencil, Trash2, Clock, Layers, BadgeDollarSign, Inbox, CheckCircle2, Mail, Send, CalendarClock, AlertTriangle, Users, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOURCE = "delivery_plus";
const aud = (n, d = 2) => `$${(Number(n) || 0).toLocaleString("en-AU", { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  customer_name: "", job_date: "", start_time: "", pickup: "", dropoff: "", mover_name: "",
  hourly_rate: "85", customer_rate: "", mover_hours: "", stairs: "", callout_hours: "", extra_amount: "", extra_reason: "", amount_paid: "", received_reason: "",
  due_date: "", invoice_total: "", paid: false, notes: "",
};

const EMPTY_SETTINGS = { email: "", bank_account_name: "", bank_bsb: "", bank_account_number: "", business_abn: "" };

export const DeliveryPlus = () => {
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [sDraft, setSDraft] = useState(EMPTY_SETTINGS);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [jr, sr] = await Promise.all([
        axios.get(`${API}/manual-jobs`, { params: { source: SOURCE } }),
        axios.get(`${API}/manual-jobs/settings`, { params: { source: SOURCE } }),
      ]);
      setJobs(jr.data.jobs || []);
      setSummary(jr.data.summary || {});
      setSettings({ ...EMPTY_SETTINGS, ...sr.data });
      setSDraft({ ...EMPTY_SETTINGS, ...sr.data });
    } catch (e) {
      toast.error("Could not load Delivery Plus");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    try {
      await axios.put(`${API}/manual-jobs/settings`, { source: SOURCE, ...sDraft });
      setSettings({ ...sDraft });
      toast.success("Delivery Plus details saved");
    } catch { toast.error("Could not save details"); }
  };
  const email = settings.email;

  const num = (v) => (v === "" || v == null ? null : Number(v));

  const save = async () => {
    const f = form;
    if (!f.customer_name.trim()) { toast.error("Customer name is required"); return; }
    const payload = {
      source: SOURCE, customer_name: f.customer_name, job_date: f.job_date, start_time: f.start_time,
      pickup: f.pickup, dropoff: f.dropoff, mover_name: f.mover_name,
      hourly_rate: num(f.hourly_rate), customer_rate: num(f.customer_rate), mover_hours: num(f.mover_hours), stairs: num(f.stairs), callout_hours: num(f.callout_hours),
      extra_amount: num(f.extra_amount), extra_reason: f.extra_reason,
      amount_paid: num(f.amount_paid), received_reason: f.received_reason, due_date: f.due_date,
      invoice_total: num(f.invoice_total), paid: !!f.paid, notes: f.notes,
    };
    try {
      if (f.id) await axios.put(`${API}/manual-jobs/${f.id}`, payload);
      else await axios.post(`${API}/manual-jobs`, payload);
      toast.success(f.id ? "Invoice updated" : "Job added");
      setForm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this job / invoice?")) return;
    try {
      await axios.delete(`${API}/manual-jobs/${id}`);
      toast.success("Deleted");
      setDetail(null);
      load();
    } catch { toast.error("Could not delete"); }
  };

  const togglePaid = async (j) => {
    try {
      const { data } = await axios.put(`${API}/manual-jobs/${j.id}`, { ...j, source: SOURCE, paid: !j.paid });
      load();
      if (detail && detail.id === j.id) setDetail(data);
    } catch { toast.error("Could not update"); }
  };

  const sendInvoice = async (j) => {
    if (!email) { toast.error("Add the Delivery Plus email at the top first"); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/manual-jobs/${j.id}/email-invoice`);
      toast.success(`Invoice emailed to ${data.to}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send invoice"); }
    finally { setSending(false); }
  };

  const Field = ({ label, k, type = "text", ph = "", hint = "" }) => (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <input
        type={type} value={form[k] ?? ""} placeholder={ph}
        onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))}
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
        data-testid={`dp-field-${k}`}
      />
      {hint && <span className="mt-0.5 block text-[9px] text-slate-400">{hint}</span>}
    </label>
  );

  const cards = [
    [Inbox, "Jobs", summary.count || 0, "text-blaze"],
    [BadgeDollarSign, "Invoiced", aud(summary.job_total || 0, 0), "text-slate-800"],
    [CheckCircle2, "Received", aud(summary.paid_total || 0, 0), "text-green-700"],
    [Clock, "Outstanding", aud(summary.outstanding || 0, 0), (summary.outstanding || 0) > 0.5 ? "text-amber-600" : "text-slate-400"],
  ];

  return (
    <div data-testid="delivery-plus-section">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">Delivery Plus</h2>
          <p className="mt-0.5 text-sm text-slate-500">Jobs tagged “Delivery Plus” auto-sync here. Rate ${85}/hr + 10% GST on labour.</p>
        </div>
        <button onClick={() => setForm({ ...EMPTY })} className="flex shrink-0 items-center gap-1.5 rounded-xl bg-blaze px-4 py-2.5 text-sm font-bold text-white active:scale-95" data-testid="dp-add-btn">
          <Plus className="h-4 w-4" /> Add job
        </button>
      </div>

      {/* Delivery Plus details: email + bank for invoices */}
      <div className="mt-4 rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
        <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500"><Mail className="h-3.5 w-3.5 text-blaze" /> Invoice details (shown on / sent with every invoice)</p>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Delivery Plus email</label>
            <input type="email" value={sDraft.email} onChange={(e) => setSDraft((s) => ({ ...s, email: e.target.value }))} placeholder="accounts@deliveryplus.com.au" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze" data-testid="dp-email-input" />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Bank account name</label>
            <input value={sDraft.bank_account_name} onChange={(e) => setSDraft((s) => ({ ...s, bank_account_name: e.target.value }))} placeholder="JBRN PTY LTD" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze" data-testid="dp-bank-name" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">BSB</label>
            <input value={sDraft.bank_bsb} onChange={(e) => setSDraft((s) => ({ ...s, bank_bsb: e.target.value }))} placeholder="063-000" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze" data-testid="dp-bank-bsb" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Account number</label>
            <input value={sDraft.bank_account_number} onChange={(e) => setSDraft((s) => ({ ...s, bank_account_number: e.target.value }))} placeholder="12345678" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze" data-testid="dp-bank-acct" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">ABN (optional)</label>
            <input value={sDraft.business_abn} onChange={(e) => setSDraft((s) => ({ ...s, business_abn: e.target.value }))} placeholder="00 000 000 000" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze" data-testid="dp-abn" />
          </div>
          <div className="col-span-2 flex justify-end">
            <button onClick={saveSettings} className="rounded-lg bg-slate-900 px-5 py-2 text-sm font-bold text-white active:scale-95" data-testid="dp-settings-save">Save details</button>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {cards.map(([Icon, label, val, cls]) => (
          <div key={label} className="rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
            <Icon className={`h-4 w-4 ${cls}`} />
            <p className={`mt-1.5 font-display text-xl font-bold ${cls}`}>{val}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
          </div>
        ))}
      </div>
      {(summary.overdue_count || 0) > 0 && (
        <p className="mt-2 flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-[12px] font-bold text-red-700" data-testid="dp-overdue-banner"><AlertTriangle className="h-4 w-4" /> {summary.overdue_count} invoice{summary.overdue_count > 1 ? "s" : ""} overdue</p>
      )}

      <div className="mt-4 space-y-2">
        {loading ? (
          <p className="py-8 text-center text-sm text-slate-400">Loading…</p>
        ) : jobs.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 py-10 text-center text-sm text-slate-400" data-testid="dp-empty">No jobs yet — tag a job as “Delivery Plus” or tap “Add job”.</p>
        ) : (
          jobs.map((j) => {
            const overdue = !j.paid && j.due_date && j.due_date < todayISO() && (j.calc_outstanding || 0) > 0.01;
            return (
              <button key={j.id} onClick={() => setDetail(j)} className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition-colors hover:border-blaze/40" data-testid={`dp-job-${j.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-slate-900">{j.customer_name || "—"} {j.synced && <span className="ml-1 rounded bg-sky-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-sky-600">Auto</span>}</p>
                  <p className="flex items-center gap-1 text-[11px] text-slate-500"><CalendarClock className="h-3 w-3" /> Due {fmtDate(j.due_date)}{j.mover_name ? ` · ${j.mover_name}` : ""}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="font-display text-sm font-bold text-slate-800">{aud(j.calc_invoice_amount || 0, 0)}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${j.paid ? "bg-green-50 text-green-700" : overdue ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{j.paid ? "Paid" : overdue ? "Overdue" : "Unpaid"}</span>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Add / edit form */}
      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto" data-testid="dp-form-modal">
          <DialogHeader><DialogTitle>{form?.id ? "Edit job / invoice" : "Add Delivery Plus job"}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Field label="Customer name" k="customer_name" ph="John Smith" /></div>
              <Field label="Job date" k="job_date" type="date" />
              <Field label="Start time" k="start_time" ph="8:00 AM" />
              <div className="col-span-2"><Field label="Pickup" k="pickup" /></div>
              <div className="col-span-2"><Field label="Drop-off" k="dropoff" /></div>
              <Field label="Mover name" k="mover_name" />
              <Field label="Our rate $/hr" k="hourly_rate" type="number" ph="85" hint="what we invoice DP" />
              <Field label="Customer rate $/hr" k="customer_rate" type="number" hint="what DP charges the customer" />
              <Field label="Hours" k="mover_hours" type="number" ph="worked hours" />
              <Field label="Call-out (hours)" k="callout_hours" type="number" hint="0.5 = 30 min · added to hours" />
              <Field label="Stairs / heavy $" k="stairs" type="number" hint="100% ours · full · no GST" />
              <Field label="Extra amount $" k="extra_amount" type="number" hint="ad-hoc charge · no GST" />
              <div className="col-span-2"><Field label="Extra is for (reason)" k="extra_reason" ph="e.g. fuel / toll / packing materials" hint="shown on the invoice line" /></div>
              <Field label="Amount received $" k="amount_paid" type="number" hint="what DP has paid us" />
              <Field label="Due date" k="due_date" type="date" hint="auto: week Sun + 12 days" />
              <div className="col-span-2"><Field label="Reason (if amount taken differs)" k="received_reason" ph="e.g. short-paid, adjustment" /></div>
              <div className="col-span-2"><Field label="Invoice total override $ (optional)" k="invoice_total" type="number" hint="leave blank to auto-calc labour + GST + extras" /></div>
              <label className="col-span-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={!!form.paid} onChange={(e) => setForm((s) => ({ ...s, paid: e.target.checked }))} data-testid="dp-field-paid" />
                Invoice paid by Delivery Plus
              </label>
              <div className="col-span-2"><Field label="Notes" k="notes" /></div>
              <div className="col-span-2 mt-1 flex gap-2">
                <button onClick={() => setForm(null)} className="flex-1 rounded-lg border border-slate-300 py-2.5 text-sm font-bold text-slate-600">Cancel</button>
                <button onClick={save} className="flex-1 rounded-lg bg-blaze py-2.5 text-sm font-bold text-white" data-testid="dp-save-btn">Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto" data-testid="dp-detail-modal">
          {detail && (() => {
            const Row = ({ label, value, cls, strong }) => (
              <div className="flex items-center justify-between text-[12px]">
                <span className={cls || "text-slate-500"}>{label}</span>
                <span className={strong ? "font-bold text-slate-900" : "font-semibold text-slate-800"}>{value}</span>
              </div>
            );
            const overdue = !detail.paid && detail.due_date && detail.due_date < todayISO() && (detail.calc_outstanding || 0) > 0.01;
            const totalH = detail.calc_total_hours != null ? detail.calc_total_hours : (detail.mover_hours || 0);
            const coH = detail.calc_callout_hours || 0;
            const hrsBreak = coH > 0 ? `${detail.mover_hours || 0} + ${coH} call-out = ${totalH}h` : `${totalH}h`;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center justify-between gap-2 pr-6">
                    <span className="truncate">{detail.customer_name || "Job"}</span>
                    <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${detail.paid ? "bg-green-50 text-green-700" : overdue ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{detail.paid ? "Paid" : overdue ? "Overdue" : "Unpaid"}</span>
                  </DialogTitle>
                  <p className="text-left text-[11px] text-slate-400">{detail.invoice_number ? `${detail.invoice_number} · ` : ""}{fmtDate(detail.job_date)}{detail.mover_name ? ` · ${detail.mover_name}` : ""}</p>
                </DialogHeader>
                {(detail.pickup || detail.dropoff) && <p className="text-[12px] text-slate-600">{detail.pickup}{detail.dropoff ? ` → ${detail.dropoff}` : ""}</p>}

                {/* Customer charge — what Delivery Plus charged the end customer */}
                <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="dp-section-customer">
                  <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500"><Users className="h-3.5 w-3.5 text-sky-600" /> Delivery Plus charged the customer</p>
                  <div className="space-y-1.5">
                    {(detail.calc_customer_rate || 0) > 0 ? (
                      <>
                        <Row label={`Labour — ${hrsBreak} × ${aud(detail.calc_customer_rate)}/hr`} value={aud(detail.calc_customer_labour || 0)} />
                        {(detail.calc_extras || 0) > 0 && <Row label="Stairs / heavy items" value={aud(detail.calc_extras)} cls="text-orange-600" />}
                        <div className="border-t border-slate-200 pt-1.5"><Row label="Customer total" value={aud(detail.calc_customer_charge || 0)} strong /></div>
                      </>
                    ) : (
                      <p className="text-[11px] text-slate-400">Add a customer rate to see what Delivery Plus charged the customer.</p>
                    )}
                  </div>
                </div>

                {/* Call-out — extra time added to the billable hours */}
                {coH > 0 && (
                  <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3" data-testid="dp-section-callout">
                    <div className="flex items-center justify-between">
                      <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-indigo-600"><Phone className="h-3.5 w-3.5" /> Call-out time</p>
                      <span className="font-display text-base font-bold text-indigo-700">{coH}h</span>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">{detail.mover_hours || 0}h worked + {coH}h call-out = <b>{totalH}h</b> billed × {aud(detail.calc_rate || 85)}/hr (+ GST).</p>
                  </div>
                )}

                {/* Section A — our payment reconciliation */}
                <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="dp-section-payment">
                  <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500"><BadgeDollarSign className="h-3.5 w-3.5 text-green-600" /> Our payment — owed vs received</p>
                  <div className="space-y-1.5">
                    <Row label={`Hours × rate (${hrsBreak} × ${aud(detail.calc_rate || 85)})`} value={aud(detail.calc_labour || 0)} />
                    <Row label="GST (10%)" value={aud(detail.calc_gst || 0)} />
                    {(detail.calc_extras || 0) > 0 && <Row label="Stairs / heavy (100% ours)" value={aud(detail.calc_extras)} cls="text-orange-600" />}
                    {(detail.calc_extra_amount || 0) > 0 && <Row label={`${detail.extra_reason || "Extra charge"} (no GST)`} value={aud(detail.calc_extra_amount)} cls="text-orange-600" />}
                    <div className="border-t border-slate-200 pt-1.5"><Row label="Total due to us" value={aud(detail.calc_invoice_amount || 0)} strong /></div>
                    <Row label="Received" value={aud(detail.amount_paid || 0)} cls="text-green-700" />
                    <Row label="Outstanding" value={aud(detail.calc_outstanding || 0)} cls={(detail.calc_outstanding || 0) > 0.01 ? "text-amber-600" : "text-slate-400"} />
                    {detail.received_reason && <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800">Reason: {detail.received_reason}</p>}
                  </div>
                </div>

                {/* Section B — invoice to Delivery Plus */}
                <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="dp-section-invoice">
                  <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500"><Inbox className="h-3.5 w-3.5 text-blaze" /> Invoice to Delivery Plus</p>
                  <div className="space-y-1.5">
                    <Row label={`Labour — ${hrsBreak} × ${aud(detail.calc_rate || 85)}/hr`} value={aud(detail.calc_labour || 0)} />
                    <Row label="GST (10% on labour)" value={aud(detail.calc_gst || 0)} />
                    {(detail.calc_extras || 0) > 0 && <Row label={<span className="flex items-center gap-1"><Layers className="h-3 w-3 text-orange-600" />Stairs / heavy (no GST)</span>} value={aud(detail.calc_extras)} cls="text-orange-600" />}
                    {(detail.calc_extra_amount || 0) > 0 && <Row label={<span className="flex items-center gap-1"><BadgeDollarSign className="h-3 w-3 text-orange-600" />{detail.extra_reason || "Extra charge"} (no GST)</span>} value={aud(detail.calc_extra_amount)} cls="text-orange-600" />}
                    <div className="border-t border-slate-200 pt-1.5"><Row label="Invoice total" value={aud(detail.calc_invoice_amount || 0)} strong /></div>
                    <Row label="Due date" value={fmtDate(detail.due_date)} cls={overdue ? "text-red-600" : "text-slate-500"} />
                    {detail.invoice_emailed_at && <Row label="Emailed" value={new Date(detail.invoice_emailed_at).toLocaleDateString("en-AU")} cls="text-slate-400" />}
                  </div>
                  <button onClick={() => sendInvoice(detail)} disabled={sending || !email} className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-blaze py-2.5 text-[12px] font-bold text-white disabled:opacity-50" data-testid="dp-email-invoice-btn">
                    <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : email ? `Email invoice to Delivery Plus` : "Add Delivery Plus email first"}
                  </button>
                </div>

                {detail.notes && <p className="rounded-lg bg-slate-50 px-3 py-2 text-[12px] text-slate-700">{detail.notes}</p>}

                <div className="flex gap-2">
                  <button onClick={() => togglePaid(detail)} className={`flex-1 rounded-lg py-2.5 text-[11px] font-bold uppercase tracking-wider ${detail.paid ? "border border-slate-300 bg-white text-slate-600" : "bg-green-600 text-white"}`} data-testid="dp-toggle-paid">{detail.paid ? "Mark unpaid" : "Mark paid"}</button>
                  <button onClick={() => { setForm({ ...detail, hourly_rate: detail.hourly_rate ?? "85", customer_rate: detail.customer_rate ?? "", callout_hours: detail.callout_hours ?? "", extra_amount: detail.extra_amount ?? "", extra_reason: detail.extra_reason ?? "", invoice_total: detail.invoice_total ?? "" }); setDetail(null); }} className="flex items-center justify-center gap-1 rounded-lg border border-slate-300 px-4 py-2.5 text-[11px] font-bold uppercase text-slate-700" data-testid="dp-edit-btn"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                  <button onClick={() => remove(detail.id)} className="flex items-center justify-center rounded-lg border border-red-200 px-4 py-2.5 text-red-600" data-testid="dp-delete-btn"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};
